/**
 * Temporary screen-scoped design tokens for **Settings → Workspaces** (the
 * workspace list and its Members, Roles, Details and Branding sub-pages).
 *
 * There is no design mock for these screens, so they are composed from the
 * language the neighbouring, already re-skinned settings screens use: the
 * Integrations list (`ApiKeys.tokens.ts` — column-header strip, row grid,
 * stadium pills), the Delegation card (`Delegation.tokens.ts` — card chrome and
 * section header) and the account Branding colour chip (`Branding.tokens.ts`).
 * Field geometry is the shared form-field box (`CustomInput` /
 * `FormField.tokens.ts`). Stays screen-local until the shared settings values
 * are promoted into `src/theme`.
 */

import formFieldMetrics from "global/components/CustomInput/FormField.tokens";
import {
  contentCard,
  formField,
  inkfree,
  notification,
  signer,
  white,
} from "theme/colors";

export const workspacesTokens = {
  /** White rounded card — identical chrome to every other Settings card. */
  card: {
    bg: white,
    border: contentCard.borderMuted,
    radius: "14px",
    shadow: "0 1px 3px rgba(33,21,71,.04)",
    padding: { xs: "18px 16px", md: "24px 28px" },
    /** Gap between the stacked blocks inside a form card. */
    gap: "18px",
    /** Gap between two stacked cards on one page. */
    stackGap: "18px",
  },

  /** Card heading + supporting sentence (Delegation's header). */
  section: {
    textGap: "3px",
    titleColor: signer.ink,
    titleFontSize: "15px",
    titleFontWeight: 800,
    titleLetterSpacing: "-0.01em",
    descriptionColor: signer.text.meta,
    descriptionFontSize: "12.5px",
    descriptionLineHeight: 1.55,
    divider: signer.border.light,
  },

  /** "← Back to workspaces" above every sub-page's card. */
  backLink: {
    color: signer.text.meta,
    hoverColor: signer.ink,
    fontSize: "12.5px",
    fontWeight: 600,
    gap: "6px",
    iconSize: 15,
    marginBottom: "16px",
  },

  /** The list card: Integrations' strip, rows and pills. */
  list: {
    color: {
      ink: signer.ink,
      body: signer.text.meta,
      muted: signer.text.faint,
      rowDivider: signer.border.divider,
      headerBg: signer.surface.subtle,
      headerBorder: signer.border.light,
      accent: signer.orange.main,
      actionHover: signer.surface.muted,
      danger: signer.danger.main,
      dangerHoverBg: signer.danger.tint,
    },
    font: {
      columnHeader: "10.5px",
      rowText: "13px",
      rowMeta: "12px",
      pill: "10.5px",
    },
    weight: { strong: 700, medium: 600, regular: 400 },
    letterSpacing: { columnHeader: "0.09em" },
    space: {
      gutter: { xs: "16px", md: "28px" },
      columnHeaderY: "10px",
      rowY: "13px",
      /** Gap between a name and the pills beside it. */
      nameGap: "8px",
      /** Gap between cells when a row stacks below `md`. */
      stackGap: "10px",
    },
    /** Column tracks per list, desktop only. */
    grid: {
      workspaces: "minmax(0, 1fr) 160px 48px",
      /** Account admins also see each workspace's envelope quota. */
      workspacesWithQuota: "minmax(0, 1fr) 160px 160px 48px",
      members: "minmax(0, 1fr) 220px 56px",
      roles: "minmax(0, 1fr) 110px 150px 96px",
    },
    actionSize: 32,
    actionIconSize: 18,
    actionGap: "4px",
    pillPadding: "2px 9px",
    pillRadius: "999px",
    skeletonRows: 3,
  },

  /** Pill tones — fill + text only; the stadium shape is shared. */
  pill: {
    neutral: { bg: signer.surface.muted, text: signer.text.meta },
    brand: { bg: inkfree.light, text: signer.orange.dark },
    warning: { bg: notification.warning.bg, text: notification.warning.text },
    /** A workspace whose envelope quota is used up. */
    danger: { bg: notification.error.bg, text: notification.error.icon },
  },

  /** Compact select sitting inside a list row (member role). */
  rowSelect: {
    height: 36,
    radius: "9px",
    fontSize: "13px",
    fontWeight: 500,
    paddingX: "12px",
  },

  /** Full form select — the shared 44px field box. */
  select: {
    height: formFieldMetrics.height,
    radius: formFieldMetrics.radius,
    fontSize: formFieldMetrics.fontSize,
    fontWeight: formFieldMetrics.fontWeight,
    paddingX: formFieldMetrics.paddingX,
    bg: formField.bg,
    border: formField.border,
    focusBorder: formField.focusBorder,
    color: formField.text,
    placeholder: formField.placeholder,
    caret: formField.adornment,
    disabledBg: formField.disabledBg,
  },

  /** Informational note ("roles are shared across workspaces"). */
  note: {
    bg: notification.info.bg,
    border: notification.info.border,
    text: notification.info.text,
    strong: notification.info.title,
    icon: notification.info.icon,
    radius: "10px",
    padding: "12px 14px",
    gap: "10px",
    fontSize: "12.5px",
    lineHeight: 1.55,
    iconSize: 17,
  },

  /** Inline contrast warnings on the branding card. */
  alert: {
    error: notification.error,
    warning: notification.warning,
  },

  /** Body copy inside modals. */
  modal: {
    bodyColor: signer.text.meta,
    bodyFontSize: "13px",
    bodyLineHeight: 1.55,
    fieldGap: "16px",
    groupLabelColor: signer.ink,
    groupLabelFontSize: formFieldMetrics.labelFontSize,
    groupLabelFontWeight: formFieldMetrics.labelFontWeight,
    checkboxLabelColor: signer.text.secondary,
    checkboxLabelFontSize: "13px",
    checkboxLabelFontWeight: 500,
    checkboxGap: "2px",
  },

  /** Workspace details / branding form cards. */
  form: {
    maxWidth: 560,
    fieldGap: "16px",
    columnGap: "18px",
    buttonRowGap: "10px",
    /** Archive / restore block under a hairline. */
    dangerTitleFontSize: "13.5px",
  },

  /** Hex read-out chip that opens the native picker (account Branding's). */
  colorChip: {
    height: formFieldMetrics.height,
    paddingX: "12px",
    radius: formFieldMetrics.radius,
    border: formField.border,
    bg: formField.bg,
    gap: "8px",
    dotSize: 18,
    dotRadius: "5px",
    dotBorder: signer.border.light,
    fontSize: "13px",
    fontWeight: 600,
    color: signer.ink,
    /** Live button preview beside the two colours. */
    previewHeight: formFieldMetrics.height,
    previewPaddingX: "18px",
    previewRadius: "10px",
    previewFontSize: "13px",
    previewFontWeight: 700,
  },

  /** Logo row on the branding card. */
  logo: {
    boxWidth: 160,
    boxHeight: 56,
    radius: "10px",
    border: formField.border,
    bg: formField.bg,
    padding: "8px",
    gap: "14px",
    emptyColor: formField.placeholderMuted,
    emptyFontSize: "12px",
  },
} as const;

export default workspacesTokens;
